"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, Sun, CloudRain, Compass } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const seasonalityTiers = [{
  id: 1,
  title: "Peak Weekend & Holiday Surge",
  period: "Oct - May (Fri to Sun & Long Weekends)",
  icon: Sun,
  highlight: "High ADR & Length-of-Stay Fencing",
  description: "Massive inflow from Mumbai and Pune driving instant sell-outs. Dynamic minimum 2-night restrictions and progressive yield ladders prevent premature weekend sell-outs at subpar rates."
}, {
  id: 2,
  title: "Monsoon & Eco-Lover Shift",
  period: "Jun - Sep (Lush Mist & Waterfall Season)",
  icon: CloudRain,
  highlight: "Value Bundling & Experience Packages",
  description: "Selective demand from trekkers and nature enthusiasts. Shift from standard discounts to high-margin inclusive meal and trail packages that safeguard baseline RevPAR."
}, {
  id: 3,
  title: "Midweek Compression Capture",
  period: "Year-Round (Monday to Thursday)",
  icon: Compass,
  highlight: "Corporate Workations & Slow Travel",
  description: "Combat the typical 35-45% midweek drop in automobile-free Matheran by targeting remote professionals, couples, and small corporate offsites through geo-targeted OTA campaigns."
}];
const challenges = [{
  id: 1,
  title: "Extreme Weekend vs. Midweek Variance",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Matheran properties face 95%+ weekend occupancy followed by steep weekday dips. We implement algorithmic minimum stay rules, dynamic weekday incentives, and corporate retreat pricing to level your monthly yield curve.",
  metricLabel: "Strategy focus",
  metricValue: "Length-of-stay fencing & midweek demand drivers"
}, {
  id: 2,
  title: "Short-Lead Booking Comp-Set Pressure",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Over 65% of Matheran bookings occur within a 48-hour window. Real-time rate scraping across MakeMyTrip, Agoda, and Booking.com ensures you capitalize on last-minute compression without panic discounting.",
  metricLabel: "Strategy focus",
  metricValue: "48-hour compression yield & intraday repricing"
}, {
  id: 3,
  title: "OTA Dependency & Direct Margin Loss",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Reclaim margins lost to 18-25% OTA commissions. We deploy automated WhatsApp booking engines, repeat traveler loyalty incentives, and rate-parity enforcement across all meta-search platforms.",
  metricLabel: "Strategy focus",
  metricValue: "Direct channel booking engine & meta parity"
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapematheran" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Matheran
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            As Asia&apos;s only automobile-free hill station situated just hours from Mumbai and Pune, Matheran presents an intense micro-market shaped by extreme weekend compression, compressed 48-hour booking windows, and distinct seasonal swings. Sunil Singh builds disciplined revenue frameworks tailored for Matheran&apos;s heritage estates, boutique resorts, and hillside retreats.
          </p>
        </div>

        {/* Seasonality Block */}
        <div className="mb-16 md:mb-20">
          <div className="border border-border/80 bg-card rounded-lg p-6 sm:p-8 lg:p-10 shadow-xs">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pb-6 mb-6 border-b border-border/60">
              <div>
                <span className="text-xs font-mono font-semibold uppercase tracking-wider text-secondary">
                  Micro-Market Intelligence
                </span>
                <h3 className="font-serif text-2xl sm:text-3xl font-bold text-foreground mt-1">
                  Matheran Seasonality & Demand Curve
                </h3>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground max-w-md">
                Tactical yield management designed around local transit pacing, toy train operations, and weather cycles.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {seasonalityTiers.map((tier, index) => {
              const IconComponent = tier.icon;
              return <div key={tier.id} data-index={index} className="flex flex-col justify-between rounded-md border border-border/60 bg-muted/20 p-5 hover:border-secondary/40 transition-[border-color] duration-300">
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="inline-flex size-9 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                          <IconComponent className="size-5 text-chart-2" />
                        </div>
                        <div>
                          <h4 className="font-serif font-bold text-base text-foreground leading-tight">
                            {tier.title}
                          </h4>
                          <span className="text-xs text-muted-foreground font-mono">
                            {tier.period}
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed mt-2">
                        {tier.description}
                      </p>
                    </div>
                    <div className="mt-4 pt-3 border-t border-border/40 text-[11px] font-medium text-secondary-foreground">
                      <span className="text-foreground font-semibold">Focus: </span>
                      {tier.highlight}
                    </div>
                  </div>;
            })}
            </div>
          </div>
        </div>

        {/* 3-Column Challenge Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Matheran hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}